import { auth } from '../firebase-config';
import {
  DashboardSummary, ExpenseBreakdownResponse, IngestionBatch, FileUploadResponse, ReviewQueueItem,
  ReviewQueueDetail, ClaimResult, ClaimResultUpdate, ClaimSubmission, User, UserCreateAdmin, UserUpdate,
  UnmatchedReceipt, ManualLinkCreate, SourceFile, AuditLog, ReportRequest, ReportResponse,
  ContractInfo, AvailablePeriod, ExceptionLog, ClaimHistoryItem, Notification, BulkIngestJob,
  BulkIngestJobDetail,
  ReportResponseItem,
  AIInsight,
  TransactionDetail,
  DashboardKpiData,
  DashboardSlaData,
  DashboardTrendData,
  EmployeeBreakdownForExpenseRequest, // Assuming this type is defined in types/index.ts
  ExpenseBreakdownForEmployeeRequest, // Assuming this type is defined in types/index.ts
  LinkMultipleReceiptsRequest,
  PaginatedClaimHistoryResponse,
  ContractUploadResponse,
  ContractUploadHistoryItem,
  ContractStatus
} from '../types';

import ENV_CONFIG from '../config/environment';
const API_URL = ENV_CONFIG.API_BASE_URL;


const fetchFile = async (endpoint: string, options: RequestInit = {}): Promise<Blob> => {
  const user = auth.currentUser;
  let token: string | null = null;
  if (user) {
    token = await user.getIdToken();
  }
  const headers: Record<string, string> = { ...options.headers as Record<string, string> };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  const response = await fetch(`${API_URL}${endpoint}`, { ...options, headers });
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ message: 'An unknown error occurred' }));
    throw new Error(errorData.detail || 'API request failed');
  }
  return response.blob();
};

const fetchApi = async <T>(endpoint: string, options: RequestInit = {}): Promise<T> => {
  const user = auth.currentUser;
  let token: string | null = null;

  if (user) {
    token = await user.getIdToken();
  }

  const headers: Record<string, string> = {
    ...(options.headers as Record<string, string>),
  };

  if (!(options.body instanceof FormData)) {
      headers['Content-Type'] = 'application/json';
  }

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const response = await fetch(`${API_URL}${endpoint}`, { ...options, headers });

  if (!response.ok) {
    if (response.status === 403) {
        throw new Error('You are not authorized to access this application. Please contact an administrator.');
    }
    if (response.status === 401) {
        throw new Error('Authentication failed. Please sign in again.');
    }
    const errorData = await response.json().catch(() => ({ message: 'An unknown error occurred' }));
    throw new Error(errorData.detail || 'API request failed');
  }
  return response.json() as Promise<T>;
};

export const apiService = {
  // Dashboard
  getDashboardKpis: (startDate: string, endDate: string): Promise<DashboardKpiData> =>
    fetchApi<DashboardKpiData>(`/dashboard/kpis?start_date=${startDate}&end_date=${endDate}`),
  getDashboardSla: (): Promise<DashboardSlaData> =>
    fetchApi<DashboardSlaData>('/dashboard/sla-breakdown'),
  getDashboardTrends: (): Promise<DashboardTrendData[]> =>
    fetchApi<DashboardTrendData[]>('/dashboard/trends-data'),

  // Ingestion
  createIngestionBatch: (): Promise<IngestionBatch> => fetchApi<IngestionBatch>('/ingestion-batches', { method: 'POST' }),
  uploadFile: (batchId: string, files: File[], type: 'claim-sheet' | 'receipts'): Promise<FileUploadResponse[]> => {
    const formData = new FormData();
    files.forEach(file => formData.append('files', file));
    const endpoint = type === 'claim-sheet' ? `/uploads/claim-sheet/${batchId}` : `/uploads/receipts/${batchId}`;
    return fetchApi(endpoint, { method: 'POST', body: formData });
  },
  processBatch: (batchId: string): Promise<{ message: string }> => fetchApi(`/ingestion-batches/${batchId}/process`, { method: 'POST' }),
  getBatchStatus: (batchId: string): Promise<IngestionBatch> => fetchApi<IngestionBatch>(`/ingestion-batches/${batchId}/status`),
  deleteFailedBatch: (batchId: string): Promise<{ message: string }> => fetchApi<{ message: string }>(`/ingestion-batches/${batchId}`, { method: 'DELETE' }),
  retryFailedBatch: (batchId: string): Promise<{ message: string }> => fetchApi(`/ingestion-batches/${batchId}/retry`, { method: 'POST' }),
  replaceAndRetryBatch: (batchId: string, claimSheet: File[], receipts: File[]): Promise<{ message: string }> => {
      const formData = new FormData();
      if (claimSheet.length > 0) formData.append('claim_sheet', claimSheet[0]);
      receipts.forEach(file => formData.append('receipts', file));
      return fetchApi(`/ingestion-batches/${batchId}/replace-and-retry`, { method: 'POST', body: formData });
  },

  // Review & Claims
  getReviewQueue: (): Promise<ReviewQueueItem[]> => fetchApi<ReviewQueueItem[]>('/review-queue'),
  getClaimHistory: (params?: URLSearchParams): Promise<PaginatedClaimHistoryResponse> => {
      const endpoint = params && params.toString() ? `/claim-history?${params.toString()}` : '/claim-history';
      return fetchApi<PaginatedClaimHistoryResponse>(endpoint);
  },
  getReviewDetails: (batchId: string): Promise<ReviewQueueDetail> => fetchApi<ReviewQueueDetail>(`/review-queue/${batchId}`),
  updateClaimResult: (resultId: string, data: ClaimResultUpdate): Promise<ClaimResult> => fetchApi<ClaimResult>(`/claim-results/${resultId}`, { method: 'PATCH', body: JSON.stringify(data) }),
  getReceiptPreviewUrl: (resultId: string): Promise<{ url: string }> => fetchApi<{ url: string }>(`/receipt-preview/${resultId}`),
  getUnmatchedReceipts: (batchId: string): Promise<UnmatchedReceipt[]> => fetchApi<UnmatchedReceipt[]>(`/review-queue/${batchId}/unmatched-receipts`),
  getUnmatchedReceiptPreviewUrl: (unmatchedReceiptId: string): Promise<{ url: string }> => fetchApi<{ url: string }>(`/unmatched-receipts/${unmatchedReceiptId}/preview`),
  manualLinkReceipt: (data: ManualLinkCreate): Promise<ClaimResult> => fetchApi<ClaimResult>('/claim-results/manual-link', { method: 'POST', body: JSON.stringify(data) }),
  // --- ADD THIS NEW FUNCTION (Step 2.2) ---
  linkMultipleReceipts: (resultId: string, data: LinkMultipleReceiptsRequest): Promise<ClaimResult> => {
    return fetchApi<ClaimResult>(`/claim-results/${resultId}/link-multiple`, {
      method: 'POST',
      body: JSON.stringify(data)
    });
  },
  // --- END NEW FUNCTION ---
  // --- ADD THIS NEW FUNCTION (To Fix Plan's Bug) ---
  getLinkedReceiptPreviewUrl: (linkedReceiptId: string): Promise<{ url: string }> => fetchApi<{ url: string }>(`/linked-receipts/${linkedReceiptId}/preview`),
  
  reopenClaim: (batchId: string): Promise<ClaimSubmission> => fetchApi<ClaimSubmission>(`/claims/${batchId}/reopen`, { method: 'POST' }),

  // User Management
  getMe: (): Promise<User> => fetchApi<User>('/users/me'),
  getAllUsers: (): Promise<User[]> => fetchApi<User[]>('/users'),
  createUser: (userData: UserCreateAdmin): Promise<User> => fetchApi<User>('/users', { method: 'POST', body: JSON.stringify(userData) }),
  updateUser: (userId: string, userData: UserUpdate): Promise<User> => fetchApi<User>(`/users/${userId}`, { method: 'PUT', body: JSON.stringify(userData) }),
  deleteUser: (userId: string): Promise<User> => fetchApi<User>(`/users/${userId}`, { method: 'DELETE' }),

  // Files & Downloads
  exportClaimsToCsv: (params?: URLSearchParams): Promise<Blob> => {
    const endpoint = params && params.toString() ? `/claim-submissions/export-csv?${params.toString()}` : '/claim-submissions/export-csv';
    return fetchFile(endpoint);
  },
  getSourceFiles: (batchId: string): Promise<SourceFile[]> => fetchApi<SourceFile[]>(`/claim-submissions/${batchId}/source-files`),
  downloadSourceFilesZip: (batchId: string): Promise<Blob> => fetchFile(`/claim-submissions/${batchId}/download-zip`),
  downloadApprovalSheet: (batchId: string): Promise<Blob> => fetchFile(`/reports/approval-sheet/batch/${batchId}`),
  downloadConsolidatedApprovalSheet: (contractId: string, month: number, year: number): Promise<Blob> => fetchFile(`/reports/approval-sheet/contract/${contractId}?month=${month}&year=${year}`),

  // Logs
  getAuditLogs: (batchId: string): Promise<AuditLog[]> => fetchApi<AuditLog[]>(`/audit-logs/${batchId}`),
  getExceptionLog: (batchId: string): Promise<ExceptionLog> => fetchApi<ExceptionLog>(`/claims/${batchId}/errors`),

  // Reports
  getExpenseBreakdown: (): Promise<ExpenseBreakdownResponse> => fetchApi<ExpenseBreakdownResponse>('/reports/expense-breakdown'),
  getContracts: (): Promise<ContractInfo[]> => fetchApi<ContractInfo[]>('/reports/approval-sheet/contracts'),
  getAvailablePeriodsForContract: (contractId: string): Promise<AvailablePeriod[]> => fetchApi<AvailablePeriod[]>(`/reports/approval-sheet/available-periods/${contractId}`),
  getExpenseSummaryReport: (reportRequest: ReportRequest): Promise<ReportResponse> => {
    return fetchApi<ReportResponse>('/reports/expense-summary', {
      method: 'POST',
      body: JSON.stringify(reportRequest),
    });
  },
  getAiSummary: (currentData: ReportResponseItem[], filters: ReportRequest): Promise<AIInsight> => {
    const payload = {
        current_data: currentData,
        filters: filters,
    };
    return fetchApi<AIInsight>('/reports/ai-summary', {
        method: 'POST',
        body: JSON.stringify(payload),
    });
  },
  getTransactionDetails: (filters: ReportRequest, categoryName: string, employeeName: string): Promise<{ data: TransactionDetail[] }> => {
    const body = {
        filters: filters,
        category_name: categoryName,
        employee_name: employeeName
    };
    return fetchApi<{ data: TransactionDetail[] }>('/reports/transaction-details', {
        method: 'POST',
        body: JSON.stringify(body)
    });
  },
  // --- THIS IS THE FIX (Part 1) ---
  // The function is renamed to match the backend endpoint and the new function is added.
  getEmployeeBreakdownForExpense: (filters: ReportRequest, expenseType: string): Promise<ReportResponse> => {
    const body: EmployeeBreakdownForExpenseRequest = {
        filters: filters,
        expense_type: expenseType
    };
    return fetchApi<ReportResponse>('/reports/employee-breakdown-for-expense', {
        method: 'POST',
        body: JSON.stringify(body)
    });
  },
  getExpenseBreakdownForEmployee: (filters: ReportRequest, employeeName: string): Promise<ReportResponse> => {
    const body: ExpenseBreakdownForEmployeeRequest = {
        filters: filters,
        employee_name: employeeName
    };
    return fetchApi<ReportResponse>('/reports/expense-breakdown-for-employee', {
        method: 'POST',
        body: JSON.stringify(body)
    });
  },
  // --- END OF FIX ---

  // Notifications
  getNotifications: (): Promise<Notification[]> => fetchApi<Notification[]>('/notifications'),
  markNotificationAsRead: (notificationId: string): Promise<Notification> => fetchApi<Notification>(`/notifications/${notificationId}/read`, { method: 'PATCH' }),
// --- THIS IS THE NEW FUNCTION ---
  markAllNotificationsAsRead: (): Promise<{ message: string }> => fetchApi('/notifications/mark-all-read', { method: 'POST' }),
// --- END OF NEW FUNCTION ---

  // Bulk Ingest
  getBulkSignedUrl: (fileName: string): Promise<{ url: string }> => fetchApi<{ url: string }>('/bulk/signed-url', { method: 'POST', body: JSON.stringify({ fileName }) }),
  getBulkJobs: (): Promise<BulkIngestJob[]> => fetchApi<BulkIngestJob[]>('/bulk/jobs'),
  getBulkJobDetails: (jobId: string): Promise<BulkIngestJobDetail> => fetchApi<BulkIngestJobDetail>(`/bulk/jobs/${jobId}`),
  retryFailedBulkJob: (jobId: string): Promise<{ message: string }> => fetchApi(`/bulk/jobs/${jobId}/retry-failed`, { method: 'POST' }),
  replaceAndRetryBulkClaim: (claimId: string, claimSheet: File[], receipts: File[]): Promise<{ message: string }> => {
      const formData = new FormData();
      formData.append('claim_sheet', claimSheet[0]);
      receipts.forEach(file => formData.append('receipts', file));
      return fetchApi(`/bulk/jobs/claims/${claimId}/replace-and-retry`, { method: 'POST', body: formData });
  },

  // Contract Admin
  getContractStatus: (): Promise<ContractStatus> => fetchApi<ContractStatus>('/admin/contracts/status'),
  downloadContractTemplate: (): Promise<Blob> => fetchFile('/admin/contracts/template'),
  exportContracts: (): Promise<Blob> => fetchFile('/admin/contracts/export'),
  uploadContracts: (file: File, dryRun: boolean = false): Promise<ContractUploadResponse> => {
    const formData = new FormData();
    formData.append('file', file);
    return fetchApi<ContractUploadResponse>(`/admin/contracts/upload${dryRun ? '?dry_run=true' : ''}`, { method: 'POST', body: formData });
  },
  getContractUploadHistory: (): Promise<ContractUploadHistoryItem[]> => fetchApi<ContractUploadHistoryItem[]>('/admin/contracts/upload-history'),
};
